import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useTenant } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { handleApiError } from "../utils/errorHandler";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { 
  BarChart3, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Shield, AlertTriangle, Bell, Users, Truck, FileText, Wallet, 
  Calendar, CreditCard, Banknote, Activity, Target, Zap, CheckCircle2,
  PieChart as PieChartIcon, Clock, ArrowRight, RefreshCw
} from "lucide-react";
import { PageLoader } from "../components/ui/page-loader";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart, LineChart, Line } from "recharts";
import { useTranslation } from "../contexts/I18nContext";

const MONTH_NAMES = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

export default function DashboardPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [enhanced, setEnhanced] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { tenantSlug } = useTenant() || {};

  const fmt = (val) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val || 0);
  const fmtPct = (val) => `${val > 0 ? '+' : ''}${(val || 0).toFixed(1)}%`;
  const goTo = (p) => navigate(tenantSlug ? `/${tenantSlug}/${p}` : `/${p}`);

  useEffect(() => {
    const load = async () => {
      try {
        const [basic, enh, al] = await Promise.all([
          api.get(`${getApiPrefix()}/dashboard/stats`).catch(() => ({data: {}})),
          api.get(`${getApiPrefix()}/analytics/dashboard-enhanced`).catch(() => ({data: {}})),
          api.get(`${getApiPrefix()}/ai/smart-alerts`).catch(() => ({data: {alerts: []}})),
        ]);
        setData(basic.data);
        setEnhanced(enh.data);
        setAlerts(al.data.alerts || []);
      } catch (err) {
        handleApiError(err, "Errore caricamento dashboard");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <PageLoader variant="section" text={t("common.loading")} />;

  const e = enhanced || {};
  const kpi = e.kpi_finanziari || {};
  const conf = e.confronto_anno_prec || {};
  const scad = e.scadenze || {};
  const trend = e.trend_mensile || [];
  const health = e.health_score ?? 100;
  const trendData = trend.map(m => ({ nome: MONTH_NAMES[m.mese - 1], ...m }));
  
  const { fatture, movimenti, patrimonio, dipendenti, progetti_attivi, costi_extra } = data || {};

  const severityColor = {
    critico: "bg-red-100 text-red-800 border-red-200",
    alto: "bg-orange-100 text-orange-800 border-orange-200",
    medio: "bg-amber-100 text-amber-800 border-amber-200",
    basso: "bg-blue-100 text-blue-800 border-blue-200",
    info: "bg-slate-100 text-slate-800 border-slate-200",
  };
  const healthColor = health >= 80 ? "text-emerald-600" : health >= 50 ? "text-amber-500" : "text-red-500";
  const healthBg = health >= 80 ? "bg-emerald-50" : health >= 50 ? "bg-amber-50" : "bg-red-50";

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("dashboard.title")}</h1>
          <p className="text-muted-foreground text-sm">Panoramica finanziaria {new Date().getFullYear()}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-sm px-3 py-1.5 ${healthBg} ${healthColor} border-0 font-semibold`} data-testid="health-score">
            <Shield className="h-3.5 w-3.5 mr-1.5" />
            Salute: {health}/100
          </Badge>
        </div>
      </div>

      {/* Smart Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="smart-alerts">
          {alerts.slice(0, 3).map((a) => (
            <div key={a.id || a.titolo} className={`flex items-center justify-between p-3 rounded-lg border ${severityColor[a.severity] || severityColor.info}`}>
              <div className="flex items-center gap-3">
                {a.severity === 'critico' ? <AlertTriangle className="h-5 w-5 shrink-0" /> : <Bell className="h-5 w-5 shrink-0" />}
                <div>
                  <p className="font-medium text-sm">{a.titolo}</p>
                  <p className="text-xs opacity-80">{a.messaggio}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* KPI Row 1: Financial */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-emerald-500 hover:shadow-md transition-all cursor-pointer" onClick={() => goTo('fatture')} data-testid="kpi-ricavi">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Ricavi Anno</p>
                <p className="text-2xl font-bold mt-1">{fmt(kpi.ricavi_anno)}</p>
                <div className="flex items-center mt-1.5">
                  {conf.var_ricavi_pct >= 0 ? (
                    <span className="text-xs text-emerald-600 flex items-center font-medium"><ArrowUpRight className="h-3 w-3 mr-0.5" />{fmtPct(conf.var_ricavi_pct)} vs anno prec.</span>
                  ) : (
                    <span className="text-xs text-red-600 flex items-center font-medium"><ArrowDownRight className="h-3 w-3 mr-0.5" />{fmtPct(conf.var_ricavi_pct)} vs anno prec.</span>
                  )}
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-400 hover:shadow-md transition-all cursor-pointer" onClick={() => goTo('fatture')} data-testid="kpi-costi">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Costi Anno</p>
                <p className="text-2xl font-bold mt-1">{fmt(kpi.costi_anno || 0)}</p>
                <div className="flex items-center mt-1.5">
                  {(conf.var_costi_pct || 0) <= 0 ? (
                    <span className="text-xs text-emerald-600 flex items-center font-medium"><ArrowDownRight className="h-3 w-3 mr-0.5" />{fmtPct(conf.var_costi_pct)}</span>
                  ) : (
                    <span className="text-xs text-red-600 flex items-center font-medium"><ArrowUpRight className="h-3 w-3 mr-0.5" />{fmtPct(conf.var_costi_pct)}</span>
                  )}
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-all cursor-pointer" onClick={() => goTo('bilancio-competenza')} data-testid="kpi-margine">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Margine Lordo</p>
                <p className={`text-2xl font-bold mt-1 ${(kpi.margine_lordo || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(kpi.margine_lordo)}</p>
                <p className="text-xs text-muted-foreground mt-1.5">Margine: {(kpi.margine_pct || 0).toFixed(1)}%</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-violet-500 hover:shadow-md transition-all cursor-pointer" onClick={() => goTo('tesoreria')} data-testid="kpi-tesoreria">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('common.treasuryBalance')}</p>
                <p className={`text-2xl font-bold mt-1 ${(e.tesoreria?.saldo_banche || 0) >= 0 ? '' : 'text-red-600'}`}>{fmt(e.tesoreria?.saldo_banche)}</p>
                <p className="text-xs text-muted-foreground mt-1.5">{kpi.fatture_non_pagate || 0} fatture aperte</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Row 2: Scadenze */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className={`hover:shadow-md transition-all cursor-pointer ${scad.scadute > 0 ? 'border-red-200 bg-red-50/30' : ''}`} onClick={() => goTo('scadenziario')} data-testid="kpi-scadute">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${scad.scadute > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                <AlertTriangle className={`h-5 w-5 ${scad.scadute > 0 ? 'text-red-600' : 'text-green-600'}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('common.expiredPlural')}</p>
                <p className={`text-xl font-bold ${scad.scadute > 0 ? 'text-red-600' : 'text-green-600'}`}>{scad.scadute || 0}</p>
                <p className="text-xs text-muted-foreground">{fmt(scad.importo_scadute)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-all cursor-pointer" onClick={() => goTo('scadenziario')} data-testid="kpi-prossime">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Prossime 30gg</p>
                <p className="text-xl font-bold text-amber-600">{scad.prossime_30gg || 0}</p>
                <p className="text-xs text-muted-foreground">{fmt(scad.importo_prossime)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-all cursor-pointer" onClick={() => goTo('scadenziario')} data-testid="kpi-residuo">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Residuo Aperto</p>
                <p className="text-xl font-bold">{fmt(kpi.residuo_aperto)}</p>
                <p className="text-xs text-muted-foreground">{kpi.fatture_non_pagate || 0} fatture non pagate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Monthly Trend */}
        <Card data-testid="chart-trend-enhanced">
          <CardHeader className="border-b bg-slate-50/50 pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4" />Andamento Ricavi vs Costi {new Date().getFullYear()}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="h-[280px]">
              {trendData.some(d => d.ricavi > 0 || d.costi !== 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(Math.abs(v)/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmt(v)} labelFormatter={(l) => `Mese: ${l}`} contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))" }} />
                    <Bar dataKey="ricavi" fill="#10b981" radius={[3,3,0,0]} name="Ricavi" />
                    <Bar dataKey="costi" fill="#ef4444" radius={[3,3,0,0]} name="Costi" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <p>{t('common.noData')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Clienti/Fornitori */}
        <Card data-testid="chart-top-entities">
          <CardHeader className="border-b bg-slate-50/50 pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="h-4 w-4" />Top 5 Clienti per Fatturato
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {(e.top_clienti || []).map((c) => {
                const maxVal = (e.top_clienti || [])[0]?.totale || 1;
                const pct = (c.totale / maxVal) * 100;
                return (
                  <div key={c.nome} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium truncate max-w-[200px]">{c.nome}</span>
                      <span className="font-semibold text-emerald-700">{fmt(c.totale)}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {(e.top_clienti || []).length === 0 && <p className="text-muted-foreground text-sm text-center py-8">Nessun cliente trovato</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Costs by CDC + Payment Status */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="chart-cdc">
          <CardHeader className="border-b bg-slate-50/50 pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <PieChartIcon className="h-4 w-4" />Costi per Centro di Costo
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {(e.costi_per_cdc || []).length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={(e.costi_per_cdc || []).map(c => ({name: c.nome, value: Math.abs(c.totale)}))} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value">
                      {(e.costi_per_cdc || []).map((c, i) => <Cell key={`cell-${c.nome}-${i}`} fill={["#6366f1","#8b5cf6","#a78bfa","#c4b5fd","#10b981","#f59e0b","#ef4444","#3b82f6"][i % 8]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <p className="text-muted-foreground text-sm text-center py-12">Nessun dato allocazione disponibile</p>}
          </CardContent>
        </Card>

        {/* Scadenze critiche */}
        <Card data-testid="upcoming-deadlines">
          <CardHeader className="border-b bg-slate-50/50 pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4" />Scadenze Imminenti
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => goTo('scadenziario')} className="text-xs">Vedi tutte <ArrowRight className="h-3 w-3 ml-1" /></Button>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="space-y-2">
              {(e.scadenze_critiche || []).length > 0 ? (e.scadenze_critiche || []).map((s) => (
                <div key={`${s.data}-${s.importo}-${s.descrizione}`} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`h-2 w-2 rounded-full shrink-0 ${new Date(s.data) < new Date() ? 'bg-red-500' : 'bg-amber-400'}`} />
                    <span className="text-sm truncate">{s.descrizione}</span>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <p className="text-sm font-semibold">{fmt(s.importo)}</p>
                    <p className="text-xs text-muted-foreground">{s.data}</p>
                  </div>
                </div>
              )) : (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                  <span className="text-sm">Nessuna scadenza imminente</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Asset overview */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Asset/Cespiti", val: patrimonio?.asset || 0, icon: BarChart3, color: "purple", path: "asset" },
          { label: "Appartamenti", val: patrimonio?.appartamenti || 0, icon: CreditCard, color: "teal", path: "appartamenti" },
          { label: "Dipendenti", val: dipendenti || 0, icon: Users, color: "orange", path: "alloggi-dipendenti" },
          { label: "Progetti Attivi", val: progetti_attivi || 0, icon: Zap, color: "pink", path: "progetti" },
        ].map((item) => (
          <Card key={item.path} className="cursor-pointer hover:shadow-md transition-all" onClick={() => goTo(item.path)} data-testid={`card-${item.path}`}>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg bg-${item.color}-100 flex items-center justify-center`}>
                  <item.icon className={`h-5 w-5 text-${item.color}-600`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{item.label}</p>
                  <p className="text-xl font-bold">{item.val}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card data-testid="quick-actions">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Azioni Rapide</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              { label: "Nuova Fattura", icon: FileText, path: "fatture" },
              { label: "Movimenti", icon: CreditCard, path: "movimenti" },
              { label: "Cash Flow", icon: Activity, path: "cash-flow" },
              { label: "Bilancio", icon: BarChart3, path: "bilancio-competenza" },
              { label: "Analisi ABC", icon: PieChartIcon, path: "analytics" },
              { label: "Approvazioni", icon: CheckCircle2, path: "approvazioni" },
              { label: "Audit Trail", icon: Shield, path: "audit-trail" },
            ].map((a) => (
              <Button key={a.path} variant="outline" size="sm" onClick={() => goTo(a.path)}>
                <a.icon className="h-3.5 w-3.5 mr-1.5" />{a.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
